import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calculator,
  FileSpreadsheet,
  FileText,
  Home,
  IndianRupee,
  LogOut,
  Receipt,
  Scale,
} from 'lucide-react';

const nav = [
  { to: '/chartered-accountant', label: 'Dashboard', icon: Home, end: true },
  { to: '/chartered-accountant/revenue', label: 'Revenue', icon: IndianRupee },
  { to: '/chartered-accountant/bills', label: 'All bills', icon: FileText },
  { to: '/chartered-accountant/expenses', label: 'Expenses', icon: Receipt },
  { to: '/chartered-accountant/receivables', label: 'Receivables', icon: IndianRupee },
  { to: '/chartered-accountant/tds', label: 'TDS', icon: Scale },
  { to: '/chartered-accountant/gst', label: 'GST', icon: FileSpreadsheet },
  { to: '/chartered-accountant/accounting', label: 'Accounting', icon: Calculator },
];

export default function CharteredAccountantLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, activeHospitalId, setActiveHospitalLocal } = useAuth();
  const hospitals = user?.hospitals ?? [];
  const showHospitalPicker = hospitals.length > 1;
  const selectedHospital = activeHospitalId ?? hospitals[0]?.id ?? '';

  const signOut = () => {
    logout();
    navigate('/login');
  };

  const active = (to: string, end?: boolean) => {
    if (end) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  return (
    <div className="min-h-screen bg-[#0c1222] text-slate-100">
      <div
        className="pointer-events-none fixed inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(ellipse 70% 45% at 50% -15%, rgba(16,185,129,0.28), transparent), radial-gradient(ellipse 50% 35% at 100% 40%, rgba(59,130,246,0.18), transparent)',
        }}
      />
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0c1222]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
              <Calculator className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-400/90">
                THMS Finance
              </p>
              <h1 className="text-lg font-semibold bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
                Chartered Accountant Portal
              </h1>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
            {showHospitalPicker && (
              <Select
                value={selectedHospital === 'all' ? 'all' : selectedHospital}
                onValueChange={(v) => setActiveHospitalLocal(v)}
              >
                <SelectTrigger className="w-[min(100%,240px)] border-white/15 bg-white/5 text-slate-100">
                  <SelectValue placeholder="Hospital" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All assigned hospitals</SelectItem>
                  {hospitals.map((h) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="hidden text-sm text-slate-400 sm:block">{user?.name}</p>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 pb-3 [-webkit-overflow-scrolling:touch]">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <Link
              key={to}
              to={to}
              className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                active(to, end)
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                  : 'text-slate-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Link>
          ))}
          <button
            type="button"
            onClick={signOut}
            className="ml-auto flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-sm text-slate-400 hover:bg-white/5 hover:text-white sm:px-4"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </nav>
      </header>
      <main className="relative mx-auto max-w-6xl px-4 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
